import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Form } from "@unform/web";
import { FormHandles } from "@unform/core";
import { Badge } from "react-bootstrap";

import {
    CurrencyInput,
    Input,
    Modal,
} from "../../../../../../../components/global";
import { ModalHandles } from "../../../../../../../components/global/modal";
import { useInvalidateCache } from "../../../../../../../shared/hooks/useInvalidateCache";
import { showToast } from "../../../../../../../components/global/toast";
import { ApiErrorHandler } from "../../../../../../../shared/utils/errors.utils";
import { yupValidation } from "../../../../../../../shared/utils/yup";
import { roadmapItemUpsertSchema } from "./schema";
import { SingleDatePicker } from "../../../../../../../components/global/single-date-picker";
import { GovPlanSecretaryProjectRoadmapItem } from "../../model/roadmap-item";
import { MODAL_LABELS_LOOKUP, ModalType } from "./lookups";
import Masks from "../../../../../../../shared/utils/mask.utils";

export type ProjectRoadmapItemDetailsModalHandles = {
    open: (context: {
        type: ModalType;
        projectId: number;
        project?: GovPlanSecretaryProjectRoadmapItem;
    }) => void;
    close: () => void;
};

const ProjectRoadmapItemDetails =
    forwardRef<ProjectRoadmapItemDetailsModalHandles>((_, ref) => {
        const invalidate = useInvalidateCache();

        const [loading, setLoading] = useState(false);
        const [modalType, setModalType] = useState<ModalType>("create");
        const [item, setItem] = useState<GovPlanSecretaryProjectRoadmapItem>();
        const [projectId, setProjectId] = useState<number>();

        const innerRef = useRef<ModalHandles>(null);
        const formRef = useRef<FormHandles>(null);

        const lookup =
            MODAL_LABELS_LOOKUP[modalType] || MODAL_LABELS_LOOKUP.create;

        const isReadonly = modalType === "view";

        const handleClose = () => {
            setItem(undefined);
            setProjectId(undefined);
            setModalType("create");
            innerRef.current?.close();
        };

        useImperativeHandle(ref, () => ({
            close: handleClose,
            open: (context) => {
                setModalType(context.type);
                innerRef.current?.open();
                if (context.project) {
                    setItem(context.project);
                    setTimeout(() => {
                        formRef.current?.setData({
                            name: context.project?.name,
                            description: context.project?.description,
                            amount: context.project?.amount,
                            observation: context.project?.observation,
                            deadline: context.project?.deadline,
                        });
                    }, 200);
                } else {
                    setProjectId(context.projectId);
                }
            },
        }));

        const handleSubmit = async (formData: {
            name: string;
            description: string;
            amount: number;
            observation?: string;
            deadline: Date;
        }) => {
            try {
                setLoading(true);

                await roadmapItemUpsertSchema.validate(formData, {
                    abortEarly: false,
                });

                if (item) {
                    await item.update(formData);

                    showToast({
                        type: "success",
                        message: "Meta do departamento atualizada com sucesso",
                    });
                } else {
                    await GovPlanSecretaryProjectRoadmapItem.create({
                        ...formData,
                        projectId: projectId as number,
                    });
                    showToast({
                        type: "success",
                        message: "Meta do departamento criada com sucesso",
                    });
                }

                invalidate();
                handleClose();
            } catch (err) {
                ApiErrorHandler(err);
                formRef.current?.setErrors(yupValidation(err));
            } finally {
                setLoading(false);
            }
        };

        return (
            <Modal ref={innerRef} title={lookup.title} size="lg">
                {isReadonly && (
                    <>
                        <Badge className="capitalize" bg={item?.variant}>
                            {item?.status}
                        </Badge>
                        <div>
                            <span className="font-semibold">Prazo total: </span>
                            {item?.intervalBetweenCreationAndDeadline()} dias
                        </div>

                        {item?.isCompleted() ? (
                            <div>
                                <span className="font-semibold">
                                    Concluido em:{" "}
                                </span>
                                {Masks.date(
                                    item.completedAt?.toISOString() as string
                                )}
                            </div>
                        ) : (
                            <div>
                                <span className="font-semibold">Restam: </span>
                                {item?.daysUntilDeadline() || ""} dias
                            </div>
                        )}
                        <hr />
                    </>
                )}
                <Form onSubmit={handleSubmit} ref={formRef}>
                    <div className="grid gap-y-6 gap-x-2 grid-cols-3">
                        <div className="col-span-3">
                            <Input
                                name="name"
                                label="Departamento"
                                className="form-input"
                                readOnly={isReadonly}
                                disabled={isReadonly}
                            />
                        </div>
                        <div className="col-span-3">
                            <Input
                                name="description"
                                label="Descrição"
                                className="form-input"
                                readOnly={isReadonly}
                                disabled={isReadonly}
                            />
                        </div>

                        <CurrencyInput
                            name="amount"
                            label="Valor para executar o projeto"
                            className="col-span-2"
                            readOnly={isReadonly}
                            disabled={isReadonly}
                        />

                        <SingleDatePicker
                            name="deadline"
                            label="Data limite"
                            readOnly={isReadonly}
                            disabled={isReadonly}
                        />

                        <div className="col-span-3">
                            <Input
                                name="observation"
                                label="Observação"
                                className="form-input"
                                readOnly={isReadonly}
                                disabled={isReadonly}
                            />
                        </div>
                    </div>
                    <div className="flex justify-between">
                        <div />
                        <button
                            className="btn-primary"
                            type={isReadonly ? "button" : "submit"}
                            disabled={loading || item?.isCompleted()}
                            onClick={(e) => {
                                if (!isReadonly) return;
                                e.preventDefault();
                                setModalType("update");
                            }}
                        >
                            {lookup.action}
                        </button>
                    </div>
                </Form>
            </Modal>
        );
    });

ProjectRoadmapItemDetails.displayName = "ProjectRoadmapItemDetails";

export { ProjectRoadmapItemDetails };
